/* tslint:disable:jsx-no-multiline-js */
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';

import Form from '../../app/common/components/Form';
import Spinner from '../../app/common/components/Spinner';
import { MQS, SHADOWS, SPACINGS } from '../../app/common/constants/styles';
import { CountryFieldContainer } from '../../app/common/fields/country/CountryFieldContainer';
import DobField from '../../app/common/fields/DobField';
import FirstNameField from '../../app/common/fields/FirstNameField';
import GenderSelectField from '../../app/common/fields/gender/GenderSelectField';
import LastNameField from '../../app/common/fields/LastNameField';
import NicknameField from '../../app/common/fields/NicknameField';
import PhoneNumberField from '../../app/common/fields/phoneNumber/PhoneNumberField';
import LastNameKanaField from '../../app/common/fields/Kana/LastNameKanaField';
import FirstNameKanaField from '../../app/common/fields/Kana/FirstNameKanaField';
import { SupportedClientCountryCode } from '../../config/constants/supportedClientCountryCode.enum';
import {
    IPersonalInfoRequest,
    IPersonalInfoResponse,
    IProfileFormFieldConfig,
} from './personalInfo.interface';
import { FormManditoryAstrixMessage } from '../../app/common/components/FormManitoryMessage';
import SortHelpers from '../../utils/sortHelpers';

import {
    destroyChallenge,
    getChallengeData,
    startChallenge,
    updateChallengerLanguageWithOutStop,
} from '../../utils/challengerMain';
import { formatPhoneNumForApiRequest } from './helpers/PersonalInfoPhoneNum';
import { SupportedLanguage } from '../../config/constants/supportedLanguage.enum';
import {
    TwoColFormLeftElement,
    TwoColFormRightElement,
    TwoColFormWrapper,
} from '../../app/common/components/FormLayoutStyles';
import HeadTitle from '../../app/common/components/HeadTitle';

const Base = styled.div`
    background-color: #fff;
    border-radius: 4px;
    box-shadow: ${SHADOWS.level1};
    padding: ${SPACINGS.base * 1.25}px ${SPACINGS.base * 6.25}px;
    padding-bottom: 40px;

    @media (max-width: ${MQS.mq2Min}) {
        padding: ${SPACINGS.base * 1.25}px ${SPACINGS.base * 3.25}px;
    }
`;

const FormWrapper = styled.div`
    position: relative;
`;

const PersonalInfoFieldWrapper = styled.div`
    margin-top: 50px;
    margin-bottom: 40px;
`;
const PersonalInfoAstrixLabelWrapper = styled.p`
    margin-bottom: 16px;
`;

const SpinnerWrapper = styled.div`
    display: flex;
    min-height: 100%;
    justify-content: center;
    align-items: center;
`;

interface IPersonalInfoProps {
    app: any; // remove any once app type exists
    personalInfoState: any; // remove any once personalInfoState type exists
    user: IPersonalInfoResponse;
    updatePersonalInfo: (data: IPersonalInfoRequest) => any;
    intl: InjectedIntl;
    visibleFieldsConfig: IProfileFormFieldConfig[];
    challengerPid: string;
    challengerDegenerate?: boolean;
    showDobAndGenderCaution: boolean;
}

interface IPersonalInfoState {
    locale: SupportedLanguage;
}

interface IPersonalInfoFormValues {
    firstName?: string;
    firstNameKana?: string;
    lastName?: string;
    lastNameKana?: string;
    nickname?: string;
    countryOfResidenceCode?: string;
    countryOfResidenceName?: string;
    gender?: 'F' | 'M';
    phoneNumber?: string;
    phoneNumberCallingCode?: string;
    phoneNumberDefaultCountry?: SupportedClientCountryCode;
    day?: string;
    month?: string;
    year?: string;
}

class PersonalInfo extends React.Component<IPersonalInfoProps, IPersonalInfoState> {
    private isChallengeLoaded: boolean;

    constructor(props: IPersonalInfoProps) {
        super(props);

        const { app, challengerPid } = this.props;

        this.state = {
            locale: app.locale,
        };

        this.isChallengeLoaded = !challengerPid;

        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.isReady = this.isReady.bind(this);
    }

    public componentDidMount() {
        window.scrollTo({ top: 0 });
        const { challengerPid } = this.props;
        startChallenge(challengerPid);
    }

    public componentWillUnmount() {
        const { challengerPid } = this.props;
        destroyChallenge(challengerPid);
    }

    public componentDidUpdate(prevProps: IPersonalInfoProps): void {
        const locale = this.props.app.locale;
        const prevLocale = prevProps.app.locale;

        if (locale !== prevLocale) {
            updateChallengerLanguageWithOutStop(locale);
        }
    }

    public isReady(): boolean {
        return this.props.app.memberLoggedIn;
    }

    public handleSubmit(values: IPersonalInfoFormValues) {
        const {
            app: {
                presentationConfig: { visibleFields },
            },
            user,
            updatePersonalInfo,
            challengerPid,
            challengerDegenerate,
        } = this.props;

        const locale = this.props.app.locale;
        const isJapanese = locale === 'ja';

        let dob = '';
        if (values.day && values.month && values.year) {
            dob = `${values.year}-${values.month}-${values.day}`;
        }

        const data: any = {};

        if (challengerPid) {
            data.challengeData = getChallengeData(challengerPid);
        }

        if (visibleFields.profile.firstName) {
            data.firstName = values.firstName;
        }
        if (visibleFields.profile.firstNameKana && isJapanese) {
            data.firstNameKana = values.firstNameKana;
        }

        if (visibleFields.profile.lastName) {
            data.lastName = values.lastName;
        }
        if (visibleFields.profile.lastNameKana && isJapanese) {
            data.lastNameKana = values.lastNameKana;
        }

        if (visibleFields.profile.nickname) {
            data.nickname = values.nickname;
        }

        if (visibleFields.profile.country) {
            data.countryOfResidence = values.countryOfResidenceCode
                ? values.countryOfResidenceCode
                : undefined;
        }

        if (visibleFields.profile.phoneNumber) {
            data.mobilePhone =
                !values.phoneNumber ||
                (values.phoneNumberCallingCode &&
                    values.phoneNumber.length <= values.phoneNumberCallingCode.length)
                    ? ''
                    : values.phoneNumber;

            data.mobilePhone = formatPhoneNumForApiRequest(data.mobilePhone, user.mobilePhone);
        }

        if (visibleFields.profile.dateOfBirth && !user.dob) {
            data.dob = dob ? dob : undefined;
        }

        if (visibleFields.profile.gender && !user.gender) {
            data.gender =
                values.gender === 'M' || values.gender === 'F' ? values.gender : undefined;
        }

        updatePersonalInfo(data);
    }

    public render() {
        const { app, user, personalInfoState, challengerPid, challengerDegenerate } = this.props;
        const dob = user.dob ? this.objectifyDate(user.dob) : { day: '', month: '', year: '' };
        const initialValues: IPersonalInfoFormValues = {
            firstName: user.firstName || '',
            firstNameKana: user.firstNameKana || '',
            lastName: user.lastName || '',
            lastNameKana: user.lastNameKana || '',
            nickname: user.nickname || '',
            countryOfResidenceCode: user.countryOfResidence || '',
            countryOfResidenceName: '',
            phoneNumber: user.mobilePhone || '',
            phoneNumberCallingCode: '',
            phoneNumberDefaultCountry: app.clientConfig.countryCode,
            gender: user.gender || undefined,
            day: dob.day,
            month: dob.month,
            year: dob.year,
        };

        return (
            <Base>
                {this.isReady() && personalInfoState ? (
                    <>
                        <HeadTitle />
                        <Form
                            buttonTextLabel={'button.save'}
                            value={initialValues}
                            handleSubmit={this.handleSubmit}
                            loading={personalInfoState.updating}
                            renderFormFields={this.renderFormFields}
                            challengerPid={challengerPid}
                            saveButtonProps={{
                                qaId: 'submit-personal-information',
                                width: '250px',
                            }}
                        />
                    </>
                ) : (
                    <SpinnerWrapper>
                        <Spinner size={50} />
                    </SpinnerWrapper>
                )}
            </Base>
        );
    }

    private renderFormFields(values: IPersonalInfoFormValues): ReactNode {
        const {
            app: {
                clientConfig: { csUrl },
                presentationConfig: { visibleFields },
            },
            user,
            visibleFieldsConfig,
        } = this.props;
        visibleFieldsConfig.sort(SortHelpers.sortProfileFieldAscendingOrder);
        const locale = this.props.app.locale;
        const countryCode = this.props.app.clientConfig.countryCode;
        const showDobAndGenderCaution = this.props.showDobAndGenderCaution;
        const isJapanese = locale === 'ja';
        const isChina = locale.startsWith('zh');
        return (
            <FormWrapper>
                <PersonalInfoAstrixLabelWrapper>
                    <FormManditoryAstrixMessage />
                </PersonalInfoAstrixLabelWrapper>
                {visibleFieldsConfig.map((itemConfig) => {
                    switch (itemConfig.fieldName) {
                        case 'lastName':
                            return isJapanese || isChina ? (
                                <TwoColFormWrapper key={itemConfig.fieldName}>
                                    <TwoColFormLeftElement>
                                        <LastNameField
                                            key="lastName"
                                            required={itemConfig.required ?? true}
                                            value={values.lastName}
                                            validatorRules={
                                                visibleFields.profile.lastName.validatorRules
                                            }
                                        />
                                    </TwoColFormLeftElement>
                                    <TwoColFormRightElement>
                                        <FirstNameField
                                            value={values.firstName}
                                            key="firstName"
                                            validatorRules={
                                                visibleFields.profile.firstName.validatorRules
                                            }
                                            required={itemConfig.required ?? true}
                                        />
                                    </TwoColFormRightElement>
                                </TwoColFormWrapper>
                            ) : (
                                <TwoColFormWrapper key={itemConfig.fieldName}>
                                    <TwoColFormLeftElement>
                                        <FirstNameField
                                            value={values.firstName}
                                            key="firstName"
                                            validatorRules={
                                                visibleFields.profile.firstName.validatorRules
                                            }
                                            required={itemConfig.required ?? true}
                                        />
                                    </TwoColFormLeftElement>
                                    <TwoColFormRightElement>
                                        <LastNameField
                                            key="lastName"
                                            required={itemConfig.required ?? true}
                                            value={values.lastName}
                                            validatorRules={
                                                visibleFields.profile.lastName.validatorRules
                                            }
                                        />
                                    </TwoColFormRightElement>
                                </TwoColFormWrapper>
                            );
                        case 'lastNameKana':
                            return (
                                isJapanese && (
                                    <TwoColFormWrapper key={itemConfig.fieldName}>
                                        <TwoColFormLeftElement>
                                            <LastNameKanaField
                                                key="lastNameKana"
                                                required={itemConfig.required ?? true}
                                                value={values.lastNameKana}
                                            />
                                        </TwoColFormLeftElement>
                                        <TwoColFormRightElement>
                                            <FirstNameKanaField
                                                value={values.firstNameKana}
                                                key="firstNameKana"
                                                required={itemConfig.required ?? true}
                                            />
                                        </TwoColFormRightElement>
                                    </TwoColFormWrapper>
                                )
                            );
                        case 'nickname':
                            return (
                                <PersonalInfoFieldWrapper key={itemConfig.fieldName}>
                                    <NicknameField
                                        value={values.nickname}
                                        key={itemConfig.fieldName}
                                        validatorRules={
                                            visibleFields.profile.nickname.validatorRules
                                        }
                                        required={itemConfig.required ?? false}
                                        tooltipText={true}
                                    />
                                </PersonalInfoFieldWrapper>
                            );
                        case 'phoneNumber':
                            return (
                                <PersonalInfoFieldWrapper key={itemConfig.fieldName}>
                                    <PhoneNumberField
                                        key={itemConfig.fieldName}
                                        callingCodeFieldId="phoneNumberCallingCode"
                                        defaultCountry={values.phoneNumberDefaultCountry}
                                        value={values.phoneNumber}
                                        validatorRules={
                                            visibleFields.profile.phoneNumber.validatorRules
                                        }
                                        label={'mobile_phone'}
                                        required={itemConfig.required ?? false}
                                    />
                                </PersonalInfoFieldWrapper>
                            );
                        case 'dateOfBirth':
                            return (
                                <PersonalInfoFieldWrapper key={itemConfig.fieldName}>
                                    <DobField
                                        key={itemConfig.fieldName}
                                        dayValue={values.day}
                                        monthValue={values.month}
                                        yearValue={values.year}
                                        disabled={Boolean(user.dob)}
                                        csUrl={csUrl}
                                        format={itemConfig.format}
                                        showCsUrl={itemConfig.showCsUrl}
                                        showDobAndGenderCaution={showDobAndGenderCaution}
                                        validatorRules={
                                            visibleFields.profile.dateOfBirth.validatorRules
                                        }
                                        required={itemConfig.required ?? false}
                                    />
                                </PersonalInfoFieldWrapper>
                            );
                        case 'gender':
                            return (
                                <PersonalInfoFieldWrapper key={itemConfig.fieldName}>
                                    <GenderSelectField
                                        key={itemConfig.fieldName}
                                        value={values.gender}
                                        disabled={Boolean(user.gender)}
                                        csUrl={csUrl}
                                        countryCode={countryCode}
                                        showCsUrl={itemConfig.showCsUrl}
                                        showDobAndGenderCaution={showDobAndGenderCaution}
                                        validatorRules={visibleFields.profile.gender.validatorRules}
                                        required={itemConfig.required ?? false}
                                        inputDisabled={true}
                                    />
                                </PersonalInfoFieldWrapper>
                            );
                        case 'country':
                            return (
                                <PersonalInfoFieldWrapper key={itemConfig.fieldName}>
                                    <CountryFieldContainer
                                        key={itemConfig.fieldName}
                                        countryCodeFieldId="countryOfResidenceCode"
                                        countryCodeFieldValue={values.countryOfResidenceCode}
                                        countryNameFieldId="countryOfResidenceName"
                                        countryNameFieldValue={values.countryOfResidenceName}
                                        validatorRules={
                                            visibleFields.profile.country.validatorRules
                                        }
                                        required={itemConfig.required ?? true}
                                    />
                                </PersonalInfoFieldWrapper>
                            );
                        default:
                            return null;
                    }
                })}
            </FormWrapper>
        );
    }

    private objectifyDate(dateString: string): { day: string; month: string; year: string } {
        const dateParts = dateString.split('-');
        const day = dateParts[2];
        const month = dateParts[1];
        const year = dateParts[0];
        return { day, month, year };
    }
}

export default injectIntl(PersonalInfo);
